import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './StartChallenge.module.scss';
import { PageWrapper } from '../../components/layout';
import { ContactPerson } from '../../components/layout';
import { Input, TypingEffect } from '../../components/ui';
import { useAppDispatch, useAppSelector } from '../../store';
import { setUserEmail } from '../../store/slices/userSlice';
import { setPaymentData } from '../../store/slices/paymentSlice';
import { startSorgenloskarte } from '../../api/reimbursement';
import { robotMessages } from '../../config/quizQuestions';

// Icons
import RobotIcon from '../../assets/icons/robotWithCard.svg';
import CheckIcon from '../../assets/icons/check.svg';
import ArrowRightIcon from '../../assets/icons/arrowRight.svg';

// Email validation
const isValidEmail = (email: string): boolean => {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regex.test(email.toLowerCase());
};

const StartChallenge: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { email } = useAppSelector((state) => state.user);

  const [emailError, setEmailError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  // Prevent back navigation
  useEffect(() => {
    const preventBackNavigation = () => {
      window.history.pushState(null, '', window.location.href);
    };
    preventBackNavigation();
    window.onpopstate = preventBackNavigation;
    return () => {
      window.onpopstate = null;
    };
  }, []);

  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError('E-Mail ist erforderlich');
      return false;
    }
    if (!isValidEmail(value)) {
      setEmailError('Falsche E-Mail');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setUserEmail(value));
    if (emailError) {
      setEmailError('');
    }
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    validateEmail(e.target.value);
  };

  const handleStart = async () => {
    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await startSorgenloskarte({
        email,
        option: 3,
      });

      // Store in Redux
      dispatch(
        setPaymentData({
          reimbursementRef: response.reimbursementRef,
          token: response.token,
          amount: String(response.amount),
          discountedAmount: response.discountedAmount ? String(response.discountedAmount) : '',
        })
      );

      // Store in sessionStorage for persistence
      sessionStorage.setItem('ref', response.reimbursementRef);
      sessionStorage.setItem('token', response.token);
      sessionStorage.setItem('email', email);
      sessionStorage.setItem('amount', String(response.amount));
      if (response.discountedAmount) {
        sessionStorage.setItem('discountedAmount', String(response.discountedAmount));
      }

      if (response.token) {
        navigate('/payment');
      }
    } catch (error) {
      console.error('Error starting Sorgenloskarte:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleStart();
    }
  };

  return (
    <PageWrapper>
      <div className={styles.startChallenge}>
        <div className={styles.content}>
          <div className={styles.leftSection}>
            <div className={styles.challenge}>
              <h1>
                3.599€ für Verhinderungspflege vorfinanziert - mit der FLEXXI Sorgen Los Karte
              </h1>
              <p>
                Melden Sie sich an und sichern Sie sich die Vorfinanzierung Ihrer
                Verhinderungspflege mit der Sorgen Los Karte. Bis zu 3.599 € jährlich ab Pflegegrad
                2 – flexibel und ohne finanzielle Vorleistung.
              </p>
            </div>

            <div className={styles.form}>
              <Input
                type="email"
                placeholder="Email Adresse"
                value={email}
                onChange={handleEmailChange}
                onBlur={handleEmailBlur}
                onKeyDown={handleKeyDown}
                error={emailError}
                maxLength={250}
                required
              />

              <button className={styles.submitButton} onClick={handleStart} disabled={isLoading}>
                Jetzt Karte kaufen & Entlastung genießen
                <img src={ArrowRightIcon} alt="" />
              </button>
            </div>
          </div>

          <div className={styles.robotSection}>
            <div className={styles.messageRobot}>
              <p>
                <TypingEffect text={robotMessages.startChallenge} speed={50} />
              </p>
            </div>
            <img src={RobotIcon} alt="FLEXX-i Robot" className={styles.robot} />
          </div>
        </div>

        <div className={styles.bottomSection}>
          <div className={styles.checkList}>
            <div className={styles.checkItem}>
              <img src={CheckIcon} alt="" />
              <p>Keine Vorauszahlung Ihrer Verhinderungspflege - für nur 41€ pro Jahr.</p>
            </div>
            <div className={styles.checkItem}>
              <img src={CheckIcon} alt="" />
              <p>Kein Papierkram mehr- wir übernehmen die Bürokratie für Sie.</p>
            </div>
            <div className={styles.checkItem}>
              <img src={CheckIcon} alt="" />
              <p>Exklusiv auf 500 Karten begrenzt</p>
            </div>
          </div>
          <div className={styles.contactWrapper}>
            <ContactPerson />
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default StartChallenge;
